import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

interface ErrorState {
  groupsData: any;
  createGroupData: any;
  codesData: any;
  createCodeData: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list_groups" | "create_group" | "list_codes" | "create_code" | null;
}

const errorInitialState: ErrorState = {
  groupsData: null,
  createGroupData: null,
  codesData: null,
  createCodeData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const createErrorGroup: any = createAsyncThunk(
  "error/createErrorGroup",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody = {
        name: payload.name,
        description: payload.description || "",
        active: payload.active !== undefined ? payload.active : true
      };

      const response = await Api.post(
        API_URLS.ERROR_MANAGEMENT.CREATE_GROUP,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllErrorGroups: any = createAsyncThunk(
  "error/getAllErrorGroups",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.ERROR_MANAGEMENT.GET_ALL_GROUPS,
        {}
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createErrorCode: any = createAsyncThunk(
  "error/createErrorCode",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody = {
        code: payload.code,
        message: payload.message,
        groupId: payload.groupId || "",
        httpStatus: payload.httpStatus || "",
        severity: payload.severity || "",
        active: payload.active !== undefined ? payload.active : true
      };

      const response = await Api.post(
        API_URLS.ERROR_MANAGEMENT.CREATE_CODE,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllErrorCodes: any = createAsyncThunk(
  "error/getAllErrorCodes",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.ERROR_MANAGEMENT.GET_ALL_CODES,
        {}
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const errorSlice = createSlice({
  name: "error",
  initialState: errorInitialState,
  reducers: {
    resetAll: (state: any) => {
      state.loading = "idle";
      state.error = null;
      state.groupsData = null;
      state.createGroupData = null;
      state.codesData = null;
      state.createCodeData = null;
      state.action = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllErrorGroups.pending, (state) => {
      state.loading = "pending";
      state.groupsData = null;
      state.error = null;
      state.action = "list_groups";
    });
    builder.addCase(getAllErrorGroups.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.groupsData = action.payload;
      state.error = null;
      state.action = "list_groups";
    });
    builder.addCase(getAllErrorGroups.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.groupsData = null;
      state.action = "list_groups";
    });

    builder.addCase(createErrorGroup.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create_group";
    });
    builder.addCase(createErrorGroup.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createGroupData = action.payload;
      state.error = null;
      state.action = "create_group";
    });
    builder.addCase(createErrorGroup.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create_group";
    });

    builder.addCase(getAllErrorCodes.pending, (state) => {
      state.loading = "pending";
      state.codesData = null;
      state.error = null;
      state.action = "list_codes";
    });
    builder.addCase(getAllErrorCodes.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.codesData = action.payload;
      state.error = null;
      state.action = "list_codes";
    });
    builder.addCase(getAllErrorCodes.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.codesData = null;
      state.action = "list_codes";
    });

    builder.addCase(createErrorCode.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create_code";
    });
    builder.addCase(createErrorCode.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createCodeData = action.payload;
      state.error = null;
      state.action = "create_code";
    });
    builder.addCase(createErrorCode.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create_code";
    });
  },
});

export const { resetAll } = errorSlice.actions;
export default errorSlice.reducer;